import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import App from './App.vue'
import router from './routes'
import vueDompurifyHTMLPlugin from 'vue-dompurify-html'
import LoadingOverlay from '@/components/feedback/LoadingOverlay.vue'
import { useLoadingStore } from '@/store/loading'
import { initializeDevToken } from '@/utils/devTokenManager'
import { initNetFunnel } from '@/utils/netfunnel.init'
import { showError, showAlert } from '@/utils/alert/alertBridge'

// Alert Modal CSS
import './assets/css/alert-modal.css'

// CSS 직접 import (index.html <link> 방식은 빌드 환경에 따라 누락될 수 있음)
import '@/assets/css/base.css'
import '@/assets/css/common.css'
import '@/assets/css/layout.css'
import '@/assets/css/content.css'
import '@/assets/css/content01.css'
import '@/assets/css/print.css'
import '@/assets/css/vote.css'


// Pinia 설정
const pinia = createPinia()
pinia.use(
  createPersistedState({
    auto: true
  })
)

const app = createApp(App)

// 개발용 토큰 초기화 (portal 앱 시작 시)
initializeDevToken()

// 전역 프린트 함수 등록
app.config.globalProperties.$print = () => window.print()

app.use(pinia)
app.use(router)
app.use(vueDompurifyHTMLPlugin)

// NetFUNNEL 초기화 (AS-IS 공통 init): 설정 완료된 후 주석 해제 예정
initNetFunnel()

router.beforeEach((to, from, next) => {
  const loading = useLoadingStore()
  loading.show('Loading...')

  // Handle OAuth callback code globally: redirect to /login with code
  try {
    const code = to.query?.code
    if (code) {
      // persist code so Login.vue can consume it
      const codeVal = Array.isArray(code) ? code[0] : code
      if (!sessionStorage.getItem('pkce_code')) {
        sessionStorage.setItem('pkce_code', codeVal)
      }

      // keep state param if present
      const state = to.query?.state
      if (state && !sessionStorage.getItem('oauth_state')) {
        sessionStorage.setItem('oauth_state', Array.isArray(state) ? state[0] : state)
      }

      // avoid infinite redirect: only redirect if not already on /login
      if (to.path !== '/login') {
        return next({ path: '/login', query: to.query })
      }
    }
  } catch(e) {
    alert(e?.serverMessage || 'OAuth 처리 중 오류가 발생했습니다.')
  }

  next()
})

router.afterEach(() => {
  const loading = useLoadingStore()
  loading.hide()
  
  // 페이지 이동 시 최상단으로 스크롤
  window.scrollTo(0, 0)
})

router.onError(() => {
  const loading = useLoadingStore()
  loading.hide()
})

// 네이버 애널리틱스 스크립트 소스
function loadNaverAnalytics() {
    if (import.meta.env.VITE_ENABLE_NAVER_ANALYTICS !== 'true') return

    const script = document.createElement('script')
    script.src = '//wcs.pstatic.net/wcslog.js'
    script.async = true
    script.onload = () => {
      window.wcs_add = window.wcs_add || {}
      window.wcs_add.wa = import.meta.env.VITE_NAVER_ANALYTICS_WA

      if (window.wcs) {
        window.wcs_do()
      }
    }
    document.body.appendChild(script)
  }

loadNaverAnalytics()
  
// 전역 컴포넌트 등록
app.component('LoadingOverlay', LoadingOverlay)

app.config.errorHandler = (err) => {
  console.error('[globalErrorHandler]', err)
  const serverMsg = err?.serverMessage || err?.responseBody?.message || err?.responseBody?.data?.message
  if (serverMsg) {
    showError(serverMsg)
  }
}

window.addEventListener('unhandledrejection', (e) => {
  const err = e.reason
  console.error('[unhandledRejection]', err)
  const serverMsg = err?.serverMessage || err?.responseBody?.message || err?.responseBody?.data?.message
  if (serverMsg) {
    e.preventDefault()
    showError(serverMsg)
  }
})

router.isReady().then(() => {
  app.mount('#app')
  window.alert = (message) => showAlert(message)
})